import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  HandCoins, Plus, Check, Pencil, Trash2, Calendar, User, Ticket, X,
} from "lucide-react";
import { toast } from "sonner";
import { DisplayName } from "@/lib/name";

function formatBR(dateStr) {
  if (!dateStr) return "";
  const [y, m, d] = dateStr.split("-");
  return `${d}/${m}/${y}`;
}
function moneyBR(v) {
  const n = Number(v || 0);
  return "R$ " + n.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function daysUntil(dateStr) {
  if (!dateStr) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const [y, m, d] = dateStr.split("-").map(Number);
  const due = new Date(y, m - 1, d);
  return Math.round((due - today) / 86400000);
}

const EMPTY = {
  emission_id: "",
  client_id: "",
  passenger_name: "",
  amount: "",
  due_date: "",
  status: "pending",
  notes: "",
  installments: 1,
  payment_type: "single",
  total_amount: "",
};

function ReceivableForm({ open, onOpenChange, editing, clients, emissions, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) {
      setForm({
        emission_id: editing.emission_id || "",
        client_id: editing.client_id || "",
        passenger_name: editing.passenger_name || "",
        amount: String(editing.amount ?? ""),
        due_date: editing.due_date || "",
        status: editing.status || "pending",
        notes: editing.notes || "",
        installments: 1,
        payment_type: editing.payment_type || "single",
        total_amount: String(editing.total_amount ?? ""),
      });
    } else {
      setForm(EMPTY);
    }
  }, [editing, open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.amount || !form.due_date) {
      toast.error("Preencha valor e data de vencimento");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        emission_id: form.emission_id || null,
        client_id: form.client_id || null,
        passenger_name: form.passenger_name.trim(),
        amount: Number(form.amount),
        due_date: form.due_date,
        status: form.status,
        notes: form.notes.trim(),
        installments: form.payment_type === "installment" ? Number(form.installments) || 1 : 1,
        payment_type: form.payment_type,
        total_amount: form.payment_type === "partial" ? Number(form.total_amount || form.amount) : null,
        paid_amount: 0,
      };
      if (editing?.receivable_id) {
        await api.put(`/receivables/${editing.receivable_id}`, payload);
        toast.success("Recebimento atualizado");
      } else {
        const { data } = await api.post("/receivables", payload);
        if (data?.count && data.count > 1) {
          toast.success(`${data.count} parcelas criadas`);
        } else {
          toast.success("Recebimento criado");
        }
      }
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="receivable-form">
        <DialogHeader>
          <DialogTitle>{editing ? "Editar recebimento" : "Novo recebimento"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Emissão vinculada (opcional)</Label>
            <Select
              value={form.emission_id || "none"}
              onValueChange={(v) => {
                if (v === "none") {
                  setForm((f) => ({ ...f, emission_id: "" }));
                  return;
                }
                const emi = emissions.find((e) => e.emission_id === v);
                setForm((f) => ({
                  ...f,
                  emission_id: v,
                  client_id: emi?.passenger_ids?.[0] || emi?.client_id || f.client_id,
                  passenger_name: emi?.passengers?.[0]?.full_name || f.passenger_name,
                  amount: f.amount || String(emi?.price || ""),
                }));
              }}
            >
              <SelectTrigger data-testid="receivable-emission">
                <SelectValue placeholder="Vincular a uma emissão" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Nenhuma</SelectItem>
                {emissions.map((e) => (
                  <SelectItem key={e.emission_id} value={e.emission_id}>
                    {e.airline} {e.pnr} · {e.origin}→{e.destination}
                    {e.passengers?.[0]?.full_name ? ` · ${e.passengers[0].full_name.split(" ")[0]}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Cliente / Passageiro</Label>
            <Select
              value={form.client_id || "none"}
              onValueChange={(v) => {
                if (v === "none") {
                  setForm((f) => ({ ...f, client_id: "" }));
                  return;
                }
                const c = clients.find((x) => x.client_id === v);
                setForm((f) => ({
                  ...f,
                  client_id: v,
                  passenger_name: c?.full_name || f.passenger_name,
                }));
              }}
            >
              <SelectTrigger data-testid="receivable-client">
                <SelectValue placeholder="Escolher cliente cadastrado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— outro (digitar nome abaixo) —</SelectItem>
                {clients.map((c) => (
                  <SelectItem key={c.client_id} value={c.client_id}>{c.full_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              value={form.passenger_name}
              onChange={(e) => setForm((f) => ({ ...f, passenger_name: e.target.value }))}
              placeholder="Nome do passageiro/pagador"
              className="mt-2"
              data-testid="receivable-passenger-name"
            />
          </div>

          {!editing && (
            <div className="rounded-xl border border-slate-200 p-3 bg-blue-50/40 space-y-3">
              <Label>Forma de cobrança</Label>
              <Select value={form.payment_type}
                onValueChange={(v) => setForm((f) => ({ ...f, payment_type: v, installments: v === "installment" ? Math.max(2, Number(f.installments) || 2) : 1 }))}>
                <SelectTrigger data-testid="receivable-payment-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="single">Valor único</SelectItem>
                  <SelectItem value="installment">Parcelado mensal (dia fixo)</SelectItem>
                  <SelectItem value="partial">Fracionado (cliente paga aos poucos)</SelectItem>
                </SelectContent>
              </Select>
              {form.payment_type === "installment" && (
                <div>
                  <Label>Número de parcelas</Label>
                  <Input type="number" min="2" max="24"
                    value={form.installments}
                    onChange={(e) => setForm((f) => ({ ...f, installments: e.target.value }))}
                    data-testid="receivable-installments" />
                  <p className="text-xs text-slate-500 mt-1">
                    A 1ª parcela vence em <b>{form.due_date || "…"}</b>, as demais no mesmo dia dos próximos meses.
                  </p>
                </div>
              )}
              {form.payment_type === "partial" && (
                <div>
                  <Label>Valor total da dívida (R$)</Label>
                  <Input type="number" step="0.01" min="0"
                    value={form.total_amount || form.amount}
                    onChange={(e) => setForm((f) => ({ ...f, total_amount: e.target.value }))}
                    data-testid="receivable-total-amount" />
                  <p className="text-xs text-slate-500 mt-1">
                    O cliente vai pagando aos poucos. Você registrará cada valor que ele pagar e o restante é atualizado automaticamente.
                  </p>
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{form.payment_type === "installment" ? "Valor por parcela (R$)" : form.payment_type === "partial" ? "Valor inicial pendente" : "Valor (R$)"}</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                required
                value={form.amount}
                onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                data-testid="receivable-amount"
              />
            </div>
            <div>
              <Label>{form.payment_type === "installment" ? "Primeiro vencimento" : "Vencimento"}</Label>
              <Input
                type="date"
                required
                value={form.due_date}
                onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))}
                data-testid="receivable-due"
              />
            </div>
          </div>

          <div>
            <Label>Status</Label>
            <Select value={form.status}
              onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
              <SelectTrigger data-testid="receivable-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pendente</SelectItem>
                <SelectItem value="paid">Pago</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Observações</Label>
            <Textarea rows={2}
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              data-testid="receivable-notes"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={saving}
              className="brand-grad text-white" data-testid="receivable-save">
              {saving ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function ReceivablesTab({ onChange }) {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [emissions, setEmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { status: filter };
      const [{ data: rec }, { data: cli }, { data: emi }] = await Promise.all([
        api.get("/receivables", { params }),
        api.get("/clients"),
        api.get("/emissions", { params: { filter: "upcoming" } }),
      ]);
      setItems(rec);
      setClients(cli);
      setEmissions(emi);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [filter]);

  const handleToggle = async (r) => {
    try {
      await api.patch(`/receivables/${r.receivable_id}/toggle`);
      toast.success(r.status === "pending" ? "Marcado como pago" : "Marcado como pendente");
      load();
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await api.delete(`/receivables/${deleting.receivable_id}`);
      toast.success("Recebimento excluído");
      setDeleting(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const totalPending = items.filter((i) => i.status === "pending")
    .reduce((s, i) => s + Number(i.amount || 0), 0);

  const handlePayment = async (r) => {
    const raw = window.prompt(
      `Registrar pagamento de "${r.passenger_name || 'cliente'}"\n\n` +
      `Total: R$ ${Number(r.total_amount || 0).toFixed(2)}\n` +
      `Já pago: R$ ${Number(r.paid_amount || 0).toFixed(2)}\n` +
      `Restante: R$ ${Number(r.amount || 0).toFixed(2)}\n\n` +
      `Quanto o cliente pagou agora? (R$)`,
      ""
    );
    if (!raw) return;
    const amount = Number(raw.replace(",", "."));
    if (isNaN(amount) || amount <= 0) {
      toast.error("Valor inválido");
      return;
    }
    try {
      await api.post(`/receivables/${r.receivable_id}/payment`, { amount });
      toast.success(`Pagamento de R$ ${amount.toFixed(2)} registrado`);
      load();
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="receivables-tab">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-4 flex-wrap">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[180px] bg-white" data-testid="receivables-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pendentes</SelectItem>
              <SelectItem value="paid">Pagos</SelectItem>
              <SelectItem value="all">Todos</SelectItem>
            </SelectContent>
          </Select>
          {filter === "pending" && items.length > 0 && (
            <div className="text-sm">
              <span className="text-slate-500">Total pendente: </span>
              <b className="text-[#0b3a82]" data-testid="receivables-total">{moneyBR(totalPending)}</b>
            </div>
          )}
        </div>
        <Button onClick={() => { setEditing(null); setOpenForm(true); }}
          className="brand-grad text-white"
          data-testid="add-receivable-button">
          <Plus className="w-4 h-4 mr-1" /> Novo recebimento
        </Button>
      </div>

      {loading ? (
        <div className="text-slate-500 text-center py-12" data-testid="receivables-loading">Carregando...</div>
      ) : items.length === 0 ? (
        <div className="soft-card p-10 text-center" data-testid="receivables-empty">
          <HandCoins className="w-12 h-12 text-slate-300 mx-auto mb-3" strokeWidth={1.4} />
          <div className="text-lg font-semibold text-slate-900">Nenhum recebimento</div>
          <p className="text-slate-500 mt-1 text-sm">Adicione valores pendentes ligados a suas emissões.</p>
        </div>
      ) : (
        <div className="soft-card overflow-hidden">
          <ul className="divide-y divide-slate-100">
            {items.map((r) => {
              const days = daysUntil(r.due_date);
              const isPaid = r.status === "paid";
              const isOverdue = !isPaid && days !== null && days < 0;
              const isUrgent = !isPaid && days !== null && days >= 0 && days <= 2;
              return (
                <li key={r.receivable_id}
                  className="p-4 flex items-start gap-3 hover:bg-slate-50/50"
                  data-testid={`receivable-${r.receivable_id}`}>
                  <button
                    type="button"
                    onClick={() => handleToggle(r)}
                    className={`mt-0.5 w-5 h-5 rounded-md border-2 grid place-items-center shrink-0 ${
                      isPaid
                        ? "bg-emerald-500 border-emerald-500 text-white"
                        : "border-slate-300 hover:border-[#0b3a82]"
                    }`}
                    title={isPaid ? "Marcar como pendente" : "Marcar como pago"}
                    data-testid={`receivable-toggle-${r.receivable_id}`}>
                    {isPaid && <Check className="w-3 h-3" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className={`font-semibold ${isPaid ? "text-slate-400 line-through" : "text-slate-900"}`}>
                        <DisplayName name={r.passenger_name || "—"} />
                      </div>
                      <span className={`text-base font-bold ${isPaid ? "text-slate-400" : "text-[#0b3a82]"}`}>
                        {moneyBR(r.amount)}
                      </span>
                      {isOverdue && (
                        <span className="text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                          vencido
                        </span>
                      )}
                      {isUrgent && (
                        <span className="text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">
                          {days === 0 ? "hoje" : days === 1 ? "amanhã" : `em ${days} dias`}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" /> Vence {formatBR(r.due_date)}
                      </span>
                      {r.installment_no && (
                        <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700">
                          {r.installment_no}/{r.installments_total}
                        </span>
                      )}
                      {r.payment_type === "partial" && (
                        <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded bg-orange-50 text-orange-700">
                          Fracionado
                        </span>
                      )}
                      {r.emission_summary && (
                        <span className="flex items-center gap-1">
                          <Ticket className="w-3 h-3" />
                          {r.emission_summary.airline} {r.emission_summary.pnr} · {r.emission_summary.route}
                        </span>
                      )}
                      {r.client_id && !r.emission_summary && (
                        <span className="flex items-center gap-1"><User className="w-3 h-3" /> cliente</span>
                      )}
                    </div>
                    {r.payment_type === "partial" && (
                      <div className="mt-2">
                        {(() => {
                          const total = Number(r.total_amount || 0);
                          const paid = Number(r.paid_amount || 0);
                          const pct = total > 0 ? Math.min(100, Math.round((paid / total) * 100)) : 0;
                          return (
                            <>
                              <div className="flex items-center gap-2 text-xs mb-1">
                                <span className="text-slate-600">{moneyBR(paid)} pago</span>
                                <span className="text-slate-400">/</span>
                                <span className="text-slate-500">{moneyBR(total)} total</span>
                                <span className="ml-auto font-semibold text-[#0b3a82]">
                                  {moneyBR(r.amount)} restante
                                </span>
                              </div>
                              <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                                <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    )}
                    {r.notes && (
                      <div className="text-xs text-slate-600 mt-2 whitespace-pre-line">{r.notes}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {r.payment_type === "partial" && !isPaid && (
                      <Button size="sm" variant="outline"
                        className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                        onClick={() => handlePayment(r)}
                        data-testid={`receivable-add-payment-${r.receivable_id}`}>
                        <Plus className="w-3.5 h-3.5" /> Pagto
                      </Button>
                    )}
                    <Button size="sm" variant="ghost"
                      onClick={() => { setEditing(r); setOpenForm(true); }}
                      data-testid={`receivable-edit-${r.receivable_id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => setDeleting(r)}
                      data-testid={`receivable-delete-${r.receivable_id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <ReceivableForm open={openForm} onOpenChange={setOpenForm}
        editing={editing} clients={clients} emissions={emissions} onSaved={load} />

      <AlertDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir recebimento?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleting?.passenger_name || "Este item"} · {moneyBR(deleting?.amount || 0)} será removido.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
